import { useEffect, useRef } from "react";
import * as AV from "../AVCore/AVCore";
import { SignalRService } from "../AVCore/SignalR";
import CustomToast from "../Hooks/useCustomToast";

const useLiveSignalR = ({
  channelId,
  setMessages,
  setJoinMessages,
  setJoinUsers,
  setReactionCounts,
  addReaction,
  formatText,
  Loc,
  leaveLive,
  userId,
  hostId,
}: any) => {
  useEffect(() => {
    if (!channelId) return;

    let isMounted = true;

    const setup = async () => {
      try {
        await SignalRService.start();

        const connection = SignalRService.connection;
        if (!connection) return;

        /* ================= REMOVE OLD LISTENERS ================= */
        connection.off("ReceiveLiveMessage");
        connection.off("ReceiveLiveReaction");
        connection.off("UserJoined");
        connection.off("UserLeft");
        connection.off("LiveEnded");

        /* ================= REGISTER LISTENERS ================= */

        // MESSAGE
        connection.on("ReceiveLiveMessage", (data: any) => {
          console.log("MESSAGE RECEIVED:", data?.msg);

          if (!isMounted) return;

          setMessages((prev: any) => [...prev, data?.msg]);

          setJoinUsers((prev: AV.ILink[]) => {
            const exists = prev.some((item) => item?.id === data?.user?.id);
            return exists ? prev : [...prev, data.user];
          });
        });

        // REACTION
        connection.on("ReceiveLiveReaction", (data: any) => {
          console.log("REACTION RECEIVED:", data);

          if (!isMounted) return;

          const { emoji, user } = data;

          addReaction(emoji, user.title);

          setReactionCounts((prev: any) => ({
            ...prev,
            [emoji]: (prev[emoji] || 0) + 1,
          }));

          setJoinUsers((prev: AV.ILink[]) => {
            const exists = prev.some((item) => item?.id === user?.id);
            return exists ? prev : [...prev, user];
          });
        });

        // USER JOIN
        connection.on("UserJoined", (data: any) => {
          console.log("USER JOINED:", data);

          if (!isMounted) return;

          const message = formatText(
            Loc.currentLang.liveStream.userJoinedTheLive,
            [data.user.title],
          );

          setJoinMessages((prev: any) => [
            ...prev,
            { id: Date.now(), name: message },
          ]);

          setJoinUsers((prev: AV.ILink[]) => {
            const exists = prev.some((item) => item?.id === data?.user?.id);
            return exists ? prev : [...prev, data.user];
          });
        });

        // USER LEFT
        connection.on("UserLeft", (data: any) => {
          console.log("USER LEFT:", data);

          if (!isMounted) return;

          const message = formatText(
            Loc.currentLang.liveStream.userLeftTheLive,
            [data.user.title],
          );

          setJoinMessages((prev: any) => [
            ...prev,
            { id: Date.now(), name: message },
          ]);

          setJoinUsers((prev: AV.ILink[]) =>
            prev.filter((item) => item?.id !== data.user?.id),
          );
        });

        // LIVE ENDED
        connection.on("LiveEnded", async () => {
          console.log("LIVE ENDED");

          if (!isMounted) return;
          if (hostId === userId) return;

          const clientError = {
            message: Loc.currentLang.liveStream.liveEndedByHost,
          };
          CustomToast({ clientError });

          await leaveLive();
        });

        /* ================= JOIN GROUP ================= */
        await SignalRService.joinLiveChat(channelId);

        console.log("Joined live group:", channelId);
      } catch (error) {
        console.error("SignalR setup error:", error);
      }
    };

    setup();

    /* ================= CLEANUP ================= */
    return () => {
      isMounted = false;

      const connection = SignalRService.connection;
      if (!connection) return;

      connection.off("ReceiveLiveMessage");
      connection.off("ReceiveLiveReaction");
      connection.off("UserJoined");
      connection.off("UserLeft");
      connection.off("LiveEnded");

      SignalRService.leaveLiveChat(channelId);

      console.log("Cleaned SignalR listeners");
    };
  }, [channelId]);
};

export default useLiveSignalR;
